import { Injectable } from '@angular/core';
// import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  async downloadPdf(html: HTMLElement, filename: string, suffix?: string, landscape = false): Promise<void> {
    const pdf = await this.convertToPDF(html, landscape);
    pdf.save(`${filename}${suffix ? '-' : ''}${suffix}.pdf`);
  }

  async getPdfOutput(html: HTMLElement, filename: string, suffix?: string, landscape = false): Promise<any> {
    const pdf = await this.convertToPDF(html, landscape);
    const formData = new FormData();
    formData.append('pdf', pdf.output('blob'), `${filename}${suffix ? '-' : ''}${suffix}`);
    return formData;
  }

  // ⚠️ Strip the data-URL prefix whatever mime it carries — FileReader stamps the file's REAL
  // type (image/gif, text/csv, message/rfc822…), and a prefix these regexes don't match is left
  // in the string, so base64ToBlob's atob throws before any request is made. The old narrow
  // patterns (image/png|jpg|jpeg; application/*) silently broke gif/bmp image uploads and every
  // text-typed file (csv, html) — found on the Attachments tab redesign, 2026-09-02.
  imageToFormData(image: any) {
    const base64ImageContent = image.replace(/^data:.*?;base64,/, '');
    const blob = this.base64ToBlob(base64ImageContent, 'image/jpeg');
    const formData = new FormData();
    formData.append('image', blob);
    return formData;
  }

  fileToFormData(file: any) {
    const base64Content = file.replace(/^data:.*?;base64,/, '');
    const blob = this.base64ToBlob(base64Content, 'application/octet-stream');
    const formData = new FormData();
    formData.append('file', blob);
    return formData;
  }

  // ⚠️ The page here is what makes a downloaded/emailed report portrait or landscape - it is
  // NOT driven by any Document record, and the report's own @page rule never reaches jsPDF.
  // Every caller defaulted to portrait, which is why a landscape report still downloaded
  // portrait (Derek, 2026-07-29). The content width follows the orientation: A4 less the
  // 10mm margins each side, so 187 portrait and 277 landscape.
  private async convertToPDF(html: HTMLElement, landscape = false): Promise<any> {
    const pdf = new jsPDF(landscape ? 'l' : 'p', 'mm', 'a4', true);
    await pdf.html(html, {
      margin: 10,
      html2canvas: {
        scale: (landscape ? 277 : 187) / html.clientWidth,
      },
      autoPaging: 'text',
    });
    const pageCount = pdf.internal.pages.length;
    pdf.deletePage(pageCount);
    if (pageCount > 2) {
      pdf.deletePage(pageCount - 1);
      pdf.deletePage(pageCount - 2);
      pdf.deletePage(pageCount - 3);
      pdf.deletePage(pageCount - 4);
      pdf.deletePage(pageCount - 5);
    }

    return pdf;
  }

  private base64ToBlob(base64, mime) {
    mime = mime || '';
    const sliceSize = 1024;
    const byteChars = window.atob(base64);
    const byteArrays = [];

    for (let offset = 0, len = byteChars.length; offset < len; offset += sliceSize) {
      const slice = byteChars.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mime });
  }
}
